<template>
  <v-container fluid>
    <v-data-table
      :options.sync="options"
      :footer-props="{ itemsPerPageOptions: [10, 25, 50, -1] }"
      :headers="computedHeders"
      :items="filteredExports"
      :single-expand="true"
    >
      <template v-slot:top>
        <v-toolbar
            class="mt-4"
            flat
        >
          <v-btn
              icon
              outlined
              @click="refreshData"
          >
            <v-tooltip
                bottom
            >
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                    v-bind="attrs"
                    v-on="on"
                    color="primary"
                >
                  mdi-refresh
                </v-icon>
              </template>
              refresh exports list
            </v-tooltip>
          </v-btn>
        </v-toolbar>
      </template>

      <template v-slot:body.prepend>
        <tr class="table-filter-row">
          <td
              v-for="header in computedHeders"
              :key="`filter-${header.value}`"
          >
            <v-select
                v-if="header.value === 'is_use_upload_date'"
                v-model="filters[header.value]"
                :items="booleanFilterItems"
                :menu-props="{ closeOnContentClick: true }"
                item-text="text"
                item-value="value"
                dense
                outlined
                clearable
                hide-details
                class="table-column-filter"
            />
            <v-select
                v-else-if="['user', 'composite_customer_name', 'schema', 'status']
                  .includes(header.value)"
                v-model="filters[header.value]"
                :items="selectFilterItems[header.value]"
                :menu-props="{ closeOnContentClick: true }"
                dense
                outlined
                clearable
                hide-details
                multiple
                chips
                small-chips
                class="table-column-filter"
            />
            <v-text-field
                v-else-if="header.value !== 'actions'"
                v-model="filters[header.value]"
                :placeholder="header.text"
                dense
                outlined
                clearable
                hide-details
                class="table-column-filter"
            />
          </td>
        </tr>
      </template>

      <template v-slot:item.user="{ item }">
          {{ `${item.user_name} (ID: ${item.user_id})` }}
      </template>

      <template v-slot:item.uri="{ item }">
        <span
            v-if="item.uri"
            @click="download(item.id)"
            class="uri"
        >
          {{ exportFilename(item.uri) }}
        </span>
      </template>

      <template v-slot:item.error="{ item }">
        <div class="error-cell">
          {{ hasAnyAbility($constants.ABILITY.CUSTOMER) ? item.public_error : item.error }}
        </div>
      </template>

      <template v-slot:item.is_use_upload_date="{ item }">
        {{ item.is_use_upload_date ? 'true' : 'false' }}
      </template>

      <template v-slot:item.created_at="{ item }">
        {{ formattedDate(item.created_at) }}
      </template>

      <template v-slot:item.updated_at="{ item }">
        {{ formattedDate(item.updated_at) }}
      </template>

      <template v-slot:item.actions="{ item }">
        <v-btn
            v-if="!hasAnyAbility($constants.ABILITY.CUSTOMER)"
            icon
            @click="remove(item.id)"
            :disabled="!['error','complete'].includes(item.status)"
        >
          <v-icon
              color="red"
          >
            mdi-delete
          </v-icon>
        </v-btn>
      </template>
    </v-data-table>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex';

export default {
  name: 'Exports',

  data: () => ({
    itemClone: {},
    exports: [],

    headers: [
      { text: 'id', value: 'id', width: '112px' },
      { text: 'uuid', value: 'uuid', width: '260px' },
      { text: 'user', value: 'user', is_private: true },
      { text: 'composite customer name', value: 'composite_customer_name', width: '240px' },
      { text: 'schema', value: 'schema' },
      { text: 'timestamp begin', value: 'timestamp_begin' },
      { text: 'timestamp end', value: 'timestamp_end' },
      { text: 'use ingestion time', value: 'is_use_upload_date', width: '88px' },
      { text: 'status', value: 'status' },
      { text: 'stage', value: 'stage' },
      { text: 'uri', value: 'uri' },
      { text: 'started at', value: 'created_at' },
      { text: 'updated at', value: 'updated_at' },
      { text: 'error', value: 'error', width: '180px' },
      {
        text: 'public_error',
        value: 'public_error',
        is_private: true,
        width: '180px',
      },
      { text: '', value: 'actions' },
    ],
    options: {
      itemsPerPage: 10,
      sortBy: ['id'],
      sortDesc: [true],
      mustSort: true,
      multiSort: false,
    },
    filters: {
      id: '',
      uuid: '',
      user: [],
      composite_customer_name: [],
      schema: [],
      timestamp_begin: '',
      timestamp_end: '',
      is_use_upload_date: null,
      status: [],
      stage: '',
      uri: '',
      created_at: '',
      updated_at: '',
      error: '',
      public_error: '',
    },
    booleanFilterItems: [
      { text: 'Yes', value: true },
      { text: 'No', value: false },
    ],

  }),

  created() {
    this.fetchData();
  },

  methods: {
    ...mapActions({
      setSnack: 'snackbar/setState',
    }),

    async fetchData() {
      await this.$requestHelpers
        .get('api/admin/exports')
        .then((response) => {
          this.exports = response.data;
        }).catch((error) => {
          if (error.response) {
            if (error.response.status === 403) {
              this.$router.push({ name: 'root' });
            }
          }
        });
    },

    async refreshData() {
      await this.fetchData();
    },

    download(id) {
      this.$requestHelpers
        .post(
          'api/admin/exportDownload',
          {
            id,
          },
          {
            responseType: 'blob',
          },
        )
        .then((response) => {
          const contentType = response.headers['content-type'];

          if (contentType.includes('application/json')) {
            const reader = new FileReader();
            reader.onload = () => {
              const json = JSON.parse(reader.result);

              this.setSnack({
                snack: true,
                color: json.status === 'error' ? 'red' : 'orange',
                text: json.message,
              });
            };
            reader.readAsText(response.data);
          } else {
            const fileURL = window.URL.createObjectURL(new Blob([response.data]));
            const fileLink = document.createElement('a');
            const headerval = response.headers['content-disposition'];
            const filename = headerval.match(/filename=(.*)/);

            fileLink.href = fileURL;
            fileLink.setAttribute('download', filename[1]);
            document.body.appendChild(fileLink);

            fileLink.click();
          }
        })
        .catch((error) => {
          const reader = new FileReader();
          reader.onload = () => {
            const json = JSON.parse(reader.result);

            this.setSnack({
              snack: true,
              color: 'red',
              text: json.message,
            });
          };
          reader.readAsText(error.response.data);
        });
    },

    remove(id) {
      this.$confirm(`Are you sure you want to delete that export (ID: ${id})?`).then((res) => {
        if (!res) return;

        this.$requestHelpers
          .delete(`api/admin/export/${id}`)
          .then((response) => {
            if (response.data.success) {
              this.setSnack({
                color: 'green',
                text: 'deleted',
              });
              this.exports = this.exports.filter((e) => e.id !== id);
            } else {
              this.setSnack({
                color: 'red',
                text: 'error',
              });
            }
          });
      });
    },

    formattedDate(date) {
      const dateObj = new Date(date);

      return dateObj.toISOString().replace('T', ' ').split('.')[0];
    },

    exportFilename(uri) {
      if (!uri) return '';

      const uriParts = String(uri).split('/');

      return uriParts[uriParts.length - 1] || '';
    },

    matchFilter(value, filter) {
      if (!filter) return true;
      return String(value ?? '').toLowerCase().includes(String(filter).toLowerCase());
    },

    parseIdFilterValues(filter) {
      return String(filter || '')
        .trim()
        .split(/[\s,]+/)
        .map((value) => value.trim())
        .filter(Boolean);
    },

    matchIdFilter(value, filter) {
      const filterValues = this.parseIdFilterValues(filter);

      if (!filterValues.length) return true;

      return filterValues.includes(String(value ?? '').trim());
    },

    toBool(value) {
      if (value === true || value === 1 || value === '1') return true;
      if (value === false || value === 0 || value === '0') return false;
      return null;
    },
  },
  computed: {
    computedHeders() {
      if (this.hasAnyAbility(this.$constants.ABILITY.CUSTOMER)) {
        return this.headers.filter((el) => el.is_private === undefined || el.is_private === false);
      }

      return this.headers;
    },

    filteredExports() {
      return this.exports.filter((item) => {
        const useUploadTimeMatched = this.filters.is_use_upload_date === null
          ? true
          : this.toBool(item.is_use_upload_date) === this.filters.is_use_upload_date;
        const userValue = `${item.user_name || ''} (ID: ${item.user_id || ''})`;
        const userMatched = this.filters.user.length === 0
          ? true
          : this.filters.user.includes(userValue);
        const compositeCustomerMatched = this.filters.composite_customer_name.length === 0
          ? true
          : this.filters.composite_customer_name.includes(item.composite_customer_name || '');
        const schemaMatched = this.filters.schema.length === 0
          ? true
          : this.filters.schema.includes(item.schema || '');
        const statusMatched = this.filters.status.length === 0
          ? true
          : this.filters.status.includes(item.status || '');
        const createdAt = item.created_at ? this.formattedDate(item.created_at) : '';
        const updatedAt = item.updated_at ? this.formattedDate(item.updated_at) : '';

        return this.matchIdFilter(item.id, this.filters.id)
          && this.matchFilter(item.uuid, this.filters.uuid)
          && userMatched
          && compositeCustomerMatched
          && schemaMatched
          && this.matchFilter(item.timestamp_begin, this.filters.timestamp_begin)
          && this.matchFilter(item.timestamp_end, this.filters.timestamp_end)
          && useUploadTimeMatched
          && statusMatched
          && this.matchFilter(item.stage, this.filters.stage)
          && this.matchFilter(this.exportFilename(item.uri), this.filters.uri)
          && this.matchFilter(createdAt, this.filters.created_at)
          && this.matchFilter(updatedAt, this.filters.updated_at)
          && this.matchFilter(item.error, this.filters.error)
          && this.matchFilter(item.public_error, this.filters.public_error);
      });
    },

    selectFilterItems() {
      return {
        user: [...new Set(this.exports.map((item) => `${item.user_name || ''} (ID: ${item.user_id || ''})`))],
        composite_customer_name: [...new Set(this.exports
          .map((item) => item.composite_customer_name)
          .filter(Boolean))],
        schema: [...new Set(this.exports.map((item) => item.schema).filter(Boolean))],
        status: [...new Set(this.exports.map((item) => item.status).filter(Boolean))],
      };
    },
  },
};
</script>

<style scoped>
.table-filter-row > td {
  padding: 4px 8px !important;
}

.table-column-filter {
  max-width: 100%;
}

.error-cell {
  white-space: normal;
  word-break: break-word;
}

.uri {
  cursor: pointer;
  text-decoration: underline;
  color: blue;
}

.uri:hover {
  text-decoration: none;
}

</style>
